import { useId } from '@floating-ui/react';
import * as React from 'react';
import { useIsomorphicEffect } from '../../../hooks';
import { CrossIcon } from '../../../icons';
import { useHoneycombTheme } from '../../../theming';

import { FixMeLater } from '../../../utils';
import { IconButton, IconButtonProps } from '../../IconButton';
import { Styler, StylerProps } from '../../Styler';
import { TypographyHeading, TypographyHeadingProps } from '../../Typography';
import { useDialogContext } from '../Dialog.context';
import { DIALOG_STATIC_STYLES } from '../Dialog.styles';
import { SvgIconProps } from '../../SvgIcon';

export interface DialogHeaderProps extends StylerProps {
    /**
     * Properties applied to the `heading` component.
     */
    HeadingProps?: TypographyHeadingProps;

    /**
     * If `true`, the component will display a close button, which when clicked, will invoke the `onClose` property of the parent `Dialog`.
     * @default false
     */
    withCloseButton?: boolean;

    /**
     * The position of the close button. Only applicable when withCloseButton={true}.
     * @default end
     */
    closeButtonPosition?: 'start' | 'end';

    /**
     * Properties applied to the close button component.
     */
    CloseButtonProps?: Partial<IconButtonProps>;

    /**
     * The icon used for the close button.
     * @default CrossIcon
     */
    closeButtonIcon?: React.ReactElement<SvgIconProps>;

    /**
     * The text for the heading. If not provided then children will be used.
     */
    title?: string;

    /**
     * If `true`, the header will have a bottom border.
     * @default true;
     */
    border?: boolean;

    /**
     * A custom icon button that will be positioned at the start of the header.
     */
    startButton?: React.ReactElement<IconButtonProps>;
}

export const DialogHeader: React.ForwardRefExoticComponent<DialogHeaderProps & React.RefAttributes<HTMLElement>> =
    React.forwardRef(function DialogHeader(
        props: DialogHeaderProps,
        ref: React.ForwardedRef<FixMeLater>
    ): React.JSX.Element | null {
        const {
            HeadingProps,
            title,
            children,
            withCloseButton = false,
            closeButtonPosition = 'end',
            CloseButtonProps,
            closeButtonIcon = <CrossIcon />,
            border = true,
            startButton,
            ...other
        } = props;

        const hasAdditionalContent = title && children;

        const { setLabelId, handleOpenChange } = useDialogContext();
        const id = useId();

        const { theme } = useHoneycombTheme();
        const dialogTheme = theme.components.themes.Dialog;

        // We want to set `aria-labelledby` on the Dialog root element
        // when this component is mounted inside it.
        useIsomorphicEffect(() => {
            setLabelId(id);
            return () => setLabelId(undefined);
        }, [id, setLabelId]);

        const handleCloseClicked = (_event: React.SyntheticEvent) => {
            handleOpenChange?.(false, undefined, 'close-clicked');
        };

        const heading = (
            <TypographyHeading
                component="h2"
                size="s"
                id={id}
                color="inherit"
                data-dialog-title={true}
                {...HeadingProps}
                key="title"
            >
                {title ?? children}
            </TypographyHeading>
        );

        const contents = [heading];
        if (withCloseButton) {
            contents[closeButtonPosition === 'start' ? 'unshift' : 'push'](
                <IconButton
                    size={dialogTheme.bar.closeButtonSize}
                    alignEdge={closeButtonPosition}
                    onClick={handleCloseClicked}
                    data-close-button={true}
                    key="close-button"
                    {...CloseButtonProps}
                >
                    {closeButtonIcon}
                </IconButton>
            );
        }

        if (startButton) {
            contents.unshift(
                React.cloneElement(startButton, {
                    size: dialogTheme.bar.closeButtonSize,
                    alignEdge: 'start',
                })
            );
        }

        return (
            <Styler
                ref={ref}
                baseStyles={[DIALOG_STATIC_STYLES.bar, DIALOG_STATIC_STYLES.header]}
                data-dialog-header={true}
                data-border={border || undefined}
                data-default-gutters={true}
                {...other}
            >
                <div data-wrapper={true}>{contents}</div>
                {hasAdditionalContent && children}
            </Styler>
        );
    });
